import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Collapse,
    IconButton,
    Stack,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MaterialTypography from '../../../../components/MaterialTypography';

const ApprovalTimeline = ({ data }) => {

    console.log(data, 'datass')
    const [expandedNodes, setExpandedNodes] = useState({});

    const toggleNode = (nodeId) => {
        setExpandedNodes(prev => ({
            ...prev,
            [nodeId]: !prev[nodeId]
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return {
            month: months[date.getMonth()],
            day: String(date.getDate()).padStart(2, '0'),
            year: date.getFullYear()
        };
    };

    const getStatusColor = (approved) => {
        if (approved === true) return '#90ee90'; // light green for approved
        if (approved === false) return '#ffcccb'; // light red for rejected
        return '#d3d3d3'; // light grey for pending/null
    };

    const renderTimelineNode = (item, index, isChild = false) => {
        const isExpanded = expandedNodes[item.id] || false;
        const hasChildren = item.approvals && item.approvals.length > 0;
        const borderColor = getStatusColor(item.approved);

        return (
            <Box key={item.id || index} sx={{ position: 'relative', mb: isChild ? 2 : 4 }}>
                {/* Timeline Line */}
                {!isChild && index !== data.length - 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: isChild ? '32px' : '50%',
                            top: '60px',
                            width: '2px',
                            height: 'calc(100% + 16px)',
                            backgroundColor: '#e0e0e0',
                            transform: isChild ? 'none' : 'translateX(-50%)',
                            zIndex: 0
                        }}
                    />
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>

                    {/* Timeline Dot */}
                    <Box
                        sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: borderColor,
                            border: `3px solid ${borderColor}`,
                            flexShrink: 0,
                            mt: 2,
                            zIndex: 1,
                            boxShadow: '0 0 0 4px #fff'
                        }}
                    />

                    {/* Content Card */}
                    <Box sx={{ flex: 1 }}>
                        <Card
                            sx={{
                                border: `2px solid ${borderColor}`,
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                cursor: hasChildren ? 'pointer' : 'default',
                                transition: 'all 0.3s ease',
                                '&:hover': hasChildren ? {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    transform: 'translateY(-2px)'
                                } : {}
                            }}
                            onClick={() => hasChildren && toggleNode(item.id)}
                        >
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box sx={{ flex: 1 }}>
                                        <MaterialTypography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                            {item.baseline_name || item.title}
                                        </MaterialTypography>

                                        {item.approved_by && (
                                            <MaterialTypography variant="caption" color="textSecondary">
                                                {item.approved_by}
                                            </MaterialTypography>
                                        )}
                                        {item.role && (
                                            <Chip
                                                label={item.role}
                                                size="small"
                                                sx={{
                                                    ml: 1,
                                                    height: '20px',
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {item.approved === true ? (
                                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                                        ) : item.approved === false ? (
                                            <CancelIcon sx={{ color: '#f44336', fontSize: '1.5rem' }} />
                                        ) : (
                                            <AccessTimeIcon sx={{ color: '#9e9e9e', fontSize: '1.5rem' }} />
                                        )}
                                        {hasChildren && (
                                            <IconButton size="small">
                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Images if present */}
                                {item.images && item.images.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                        {item.images.map((img, idx) => (
                                            <Box
                                                key={idx}
                                                component="img"
                                                src={img}
                                                alt={`Image ${idx + 1}`}
                                                sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Child Timeline Items */}
                        {hasChildren && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ ml: 4, mt: 2, position: 'relative' }}>
                                    {/* Child timeline line */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: '46px',
                                            top: 0,
                                            width: '2px',
                                            height: '100%',
                                            backgroundColor: '#e0e0e0',
                                            zIndex: 0
                                        }}
                                    />
                                    {item.approvals.map((approval, idx) => (
                                        <Box key={approval.id || idx} sx={{ mb: 2, position: 'relative' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                {/* Child Date */}
                                                <Box sx={{ minWidth: '80px', textAlign: 'left', pt: 1 }}>
                                                    {approval.approved_date && (() => {
                                                        const childDate = formatDate(approval.approved_date);
                                                        return (
                                                            <>
                                                                <MaterialTypography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                                                    {childDate.month}
                                                                </MaterialTypography>
                                                                <MaterialTypography variant="h6" fontWeight="bold" color="dark">
                                                                    {childDate.day}
                                                                </MaterialTypography>
                                                                <MaterialTypography variant="caption" color="textSecondary">
                                                                    {childDate.year}
                                                                </MaterialTypography>
                                                            </>
                                                        );
                                                    })()}
                                                </Box>

                                                {/* Child Dot */}
                                                <Box
                                                    sx={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(approval.approved),
                                                        border: `2px solid ${getStatusColor(approval.approved)}`,
                                                        flexShrink: 0,
                                                        mt: 2,
                                                        zIndex: 1,
                                                        boxShadow: '0 0 0 3px #fff'
                                                    }}
                                                />

                                                {/* Child Card */}
                                                <Box sx={{ flex: 1 }}>
                                                    <Card
                                                        sx={{
                                                            border: `2px solid ${getStatusColor(approval.approved)}`,
                                                            borderRadius: '8px',
                                                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Box>
                                                                    <MaterialTypography variant="body2" fontWeight="medium">
                                                                        {approval.approved_by}
                                                                    </MaterialTypography>
                                                                    {(approval.role || approval.role_name) && (
                                                                        <Chip
                                                                            label={approval.role || approval.role_name}
                                                                            size="small"
                                                                            sx={{
                                                                                mt: 0.5,
                                                                                height: '18px',
                                                                                fontSize: '0.65rem'
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {approval.comments && (
                                                                        <MaterialTypography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                            {approval?.comments}
                                                                        </MaterialTypography>
                                                                    )}
                                                                </Box>
                                                                {approval.approved === true ? (
                                                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                                                                ) : approval.approved === false ? (
                                                                    <CancelIcon sx={{ color: '#f44336', fontSize: '1.2rem' }} />
                                                                ) : (
                                                                    <AccessTimeIcon sx={{ color: '#9e9e9e', fontSize: '1.2rem' }} />
                                                                )}
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    if (!data || data.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <MaterialTypography variant="body1" color="textSecondary">
                    No approval history available
                </MaterialTypography>
            </Box>
        );
    }

    return (
        <Box sx={{
            py: 2,
            px: 1,
            '&::-webkit-scrollbar': {
                display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
        }}>
            {data.map((item, index) => renderTimelineNode(item, index))}
        </Box>
    );
};

export default ApprovalTimeline;
